import { useEffect } from "react";
import "@/App.css";
import Lenis from "lenis";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import ServiceAreaPage from "@/pages/ServiceAreaPage";
import ServiceAreasHub from "@/pages/ServiceAreasHub";
import Builders from "@/pages/Builders";
import ContactPage from "@/pages/ContactPage";
import NotFound from "@/pages/NotFound";
import { trackPageView, trackPhoneClick } from "@/lib/analytics";

function ScrollAndTrack() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    }
    // GA4 page_view on SPA navigation (defer so document.title is updated by Helmet)
    const t = setTimeout(() => trackPageView(location.pathname + location.search), 120);
    return () => clearTimeout(t);
  }, [location.pathname, location.search, location.hash]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // Smooth scroll for same-page hash links + phone-call tracking.
    const handleClick = (e) => {
      const tel = e.target.closest('a[href^="tel:"]');
      if (tel) trackPhoneClick();

      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const id = anchor.getAttribute("href");
      if (id.length <= 1) return;
      const el = document.querySelector(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -80 });
      }
    };
    document.addEventListener("click", handleClick);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", handleClick);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollAndTrack />
        <div className="App font-body">
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/service-areas" element={<ServiceAreasHub />} />
            <Route path="/service-areas/:slug" element={<ServiceAreaPage />} />
            <Route path="/builders" element={<Builders />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
